/*******************************************************
* Client Error Context
*
* Singleton module that holds the current application context for enriching
* client error logs. React context values are synced in by ContextRefresher
* (in Providers), and browser environment info is gathered on demand.
*
* Key Features:
* - Stores React context values (user, job, PG) without requiring hooks
* - Gathers browser environment info (screen, device, network)
* - Consumed by logClientError() and BlockingErrorMessage for rich error reports
*
* Primary Operations:
* - syncContext(): Called by ContextRefresher to push React context values
* - getErrorContext(): Returns the full snapshot for DB logging (SessionData)
* - generateErrorReport(): Produces the human-readable text users copy/paste
*
* Usage:
* ```typescript
* import { getErrorContext, generateErrorReport } from './clientErrorContext';
*
* // Get structured context for DB logging
* const ctx = getErrorContext();
*
* // Get copyable text report
* const report = generateErrorReport(error);
* ```
********************************************************/
//########## IMPORTS ##########
//#region IMPORTS

//#endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
interface IdLabel {
    id: number;
    label: string;
}

export interface AppContextSnapshot {
    userName: string | null;
    userID: number | null;
    roles: IdLabel[];
    disciplines: IdLabel[];
    teamName: string | null;
    associatedTeams: IdLabel[];
    activeJob: { id: number; jobNumber: string; jobName: string } | null;
    activePG: { id: number; pgName: string } | null;
}

export interface ClientErrorContext {
    appVersion: string;
    timestamp: string;
    url: string;
    user: {
        name: string | null;
        id: number | null;
        roles: IdLabel[];
        disciplines: IdLabel[];
        team: string | null;
        associatedTeams: IdLabel[];
    };
    job: {
        activeJob: { id: number; jobNumber: string; jobName: string } | null;
        activePG: { id: number; pgName: string } | null;
    };
    browser: {
        screenSize: string;
        viewportSize: string;
        deviceType: string;
        networkInfo: string | null;
        pageOpenDuration: string;
        userAgent: string;
    };
}
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
const pageLoadedAt = typeof window !== 'undefined' ? new Date() : null;

let contextSnapshot: AppContextSnapshot = {
    userName: null,
    userID: null,
    roles: [],
    disciplines: [],
    teamName: null,
    associatedTeams: [],
    activeJob: null,
    activePG: null,
};
//#endregion


//########## FUNCTIONS ##########
//#region FUNCTIONS

/** Called by ContextRefresher to push React context values into the singleton */
export function syncContext(values: AppContextSnapshot): void {
    contextSnapshot = values;
}

/** Returns formatted duration string from page load time */
function getPageOpenDuration(): string {
    if (!pageLoadedAt) return 'Unknown';
    const ms = Date.now() - pageLoadedAt.getTime();
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
}

/** Detects touch-primary device vs desktop */
function getDeviceType(): string {
    if (typeof window === 'undefined') return 'Unknown';
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isNarrow = window.innerWidth < 768;
    if (hasTouch && isNarrow) return 'Mobile';
    if (hasTouch) return 'Tablet/Touch';
    return 'Desktop';
}

/** Gets network connection info if available */
function getNetworkInfo(): string | null {
    if (typeof navigator === 'undefined') return null;
    const conn = (navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number; rtt?: number } }).connection;
    if (!conn) return null;
    const parts: string[] = [];
    if (conn.effectiveType) parts.push(conn.effectiveType);
    if (conn.downlink) parts.push(`${conn.downlink}Mbps`);
    if (conn.rtt) parts.push(`${conn.rtt}ms RTT`);
    return parts.length > 0 ? parts.join(', ') : null;
}

/** Returns the full error context snapshot for logging */
export function getErrorContext(): ClientErrorContext {
    const isBrowser = typeof window !== 'undefined';
    return {
        appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? 'Unknown',
        timestamp: new Date().toISOString(),
        url: isBrowser ? window.location.href : 'Unknown',
        user: {
            name: contextSnapshot.userName,
            id: contextSnapshot.userID,
            roles: contextSnapshot.roles,
            disciplines: contextSnapshot.disciplines,
            team: contextSnapshot.teamName,
            associatedTeams: contextSnapshot.associatedTeams,
        },
        job: {
            activeJob: contextSnapshot.activeJob,
            activePG: contextSnapshot.activePG,
        },
        browser: {
            screenSize: isBrowser ? `${screen.width}x${screen.height}` : 'SSR',
            viewportSize: isBrowser ? `${window.innerWidth}x${window.innerHeight}` : 'SSR',
            deviceType: getDeviceType(),
            networkInfo: getNetworkInfo(),
            pageOpenDuration: getPageOpenDuration(),
            userAgent: isBrowser ? navigator.userAgent : 'Unknown',
        },
    };
}

/** Generates the human-readable error report users copy/paste to Teams */
export function generateErrorReport(error?: Error): string {
    const ctx = getErrorContext();

    let report = '=== ERROR REPORT ===\n\n';

    report += `App Version: ${ctx.appVersion}\n`;
    report += `Timestamp: ${ctx.timestamp}\n`;
    report += `URL: ${ctx.url}\n\n`;

    report += '--- User Context ---\n';
    report += `User: ${ctx.user.name ?? 'Unknown'} (ID: ${ctx.user.id ?? 'Unknown'})\n`;
    report += `Team: ${ctx.user.team ?? 'None'}\n`;
    if (ctx.user.associatedTeams.length > 0) {
        report += `Associated Teams: ${ctx.user.associatedTeams.map(t => `${t.label} (${t.id})`).join(', ')}\n`;
    }
    report += `Roles: ${ctx.user.roles.map(r => `${r.label} (${r.id})`).join(', ') || 'None'}\n`;
    report += `Disciplines: ${ctx.user.disciplines.map(d => `${d.label} (${d.id})`).join(', ') || 'None'}\n`;
    const job = ctx.job.activeJob;
    report += `Active Job: ${job ? `${job.jobName} [${job.jobNumber}] (ID: ${job.id})` : 'None'}\n`;
    const pg = ctx.job.activePG;
    report += `Active PG: ${pg ? `${pg.pgName} (ID: ${pg.id})` : 'None'}\n\n`;

    report += '--- Browser Info ---\n';
    report += `Screen: ${ctx.browser.screenSize} (viewport: ${ctx.browser.viewportSize})\n`;
    report += `Device: ${ctx.browser.deviceType}\n`;
    if (ctx.browser.networkInfo) {
        report += `Network: ${ctx.browser.networkInfo}\n`;
    }
    report += `Page Open For: ${ctx.browser.pageOpenDuration}\n`;
    report += `User Agent: ${ctx.browser.userAgent}\n\n`;

    report += '--- Error Details ---\n';
    if (error) {
        report += `Name: ${error.name}\n`;
        report += `Message: ${error.message}\n`;
        if (error.stack) {
            report += `\n--- Stack Trace ---\n`;
            report += error.stack;
        }
    } else {
        report += 'No error object available\n';
    }

    report += '\n\n=== END REPORT ===';
    return report;
}

//#endregion
